import {
  trackChatCtaClicked,
  trackDesignLabCtaClicked,
  trackPinningSessionChatOpened
} from "@design-lab/shared/lib/analytics";
import { MessageCircleMore, Save } from "lucide-react";
import { useEffect, useRef, useState } from "react";
// import { useInactivityDetector } from "@design-lab/shared/lib/useInactivityDetector";
import { currentPinningSessionAtom, pinningModeAtom, pinsAtom } from "@design-lab/entities/pinning";
import { getUserType } from "@design-lab/entities/user";
import { useAddToCartModal, useEditCart } from "@design-lab/features";
import { useSaveProject } from "@design-lab/features/save-project/hooks/useSaveProject";
import { cn } from "@design-lab/shared";
import { useEditMode } from "@design-lab/shared/hooks/useEditMode";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useValidation } from "@design-lab/shared/hooks/useValidation";
import { useIsSample } from "@design-lab/shared/lib/config";
import { addToCartLoadingAtom, saveDesignLoadingAtom } from "@design-lab/shared/state";
import { Button } from "@design-lab/shared/ui";
import { Tooltip, TooltipTrigger } from "@design-lab/shared/ui/design-system/Tooltip";
import { useAtomValue } from "jotai";

export function ChatNow() {
  const { isDesktop } = useResponsive();
  const isChatVisible = useRef(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const userType = getUserType();
  const isSample = useIsSample();

  const isPinningMode = useAtomValue(pinningModeAtom);
  const currentPinningSession = useAtomValue(currentPinningSessionAtom);
  const pins = useAtomValue(pinsAtom);

  const validation = useValidation();
  const { isEditMode } = useEditMode();
  const { isAddingToCart } = useAddToCartModal();
  const { isEditingCart } = useEditCart();
  const isSaveDesignLoading = useAtomValue(saveDesignLoadingAtom);
  const isAddToCartLoading = useAtomValue(addToCartLoadingAtom);
  const isSubmitting = isEditMode ? isEditingCart : isAddingToCart || isAddToCartLoading;

  const { setIsSaveProjectVisible } = useSaveProject();

  const isSaveDesignDisabled =
    isSubmitting ||
    isSaveDesignLoading ||
    !validation.isArtworkUploaded ||
    validation.isSomeArtworkLoading;

  // Use inactivity detector to show tooltip twice after periods of inactivity
  // useInactivityDetector({
  //   interval: 20_000,
  //   maxCallCount: 2, // Show tooltip only twice
  //   onInactive: () => setShowTooltip(true),
  //   delayDetection: 20_000 // Wait 20 seconds before starting inactivity detection
  // });

  useEffect(() => {
    const handleMinimize = () => {
      window.tidioChatApi?.hide?.();
    };

    // hide the chat widget initially
    const interval = setInterval(() => {
      if (window.tidioChatApi) {
        clearInterval(interval);
        window.tidioChatApi.hide?.();
        isChatVisible.current = false;

        window.tidioChatApi.on("close", handleMinimize);
      }
    }, 500);

    const handleDocumentClick = (event: MouseEvent) => {
      const chatWidget = document.getElementById("tidio-chat");

      if (chatWidget && isChatVisible.current && !chatWidget.contains(event.target as Node)) {
        window.tidioChatApi?.hide?.();
        isChatVisible.current = false;
      }
    };

    document.addEventListener("click", handleDocumentClick);

    return () => {
      clearInterval(interval);
      document.removeEventListener("click", handleDocumentClick);
      window.tidioChatApi?.off?.("close", handleMinimize);
    };
  }, []);

  const onChatClickHandler = () => {
    // Hide tooltip when chat is clicked
    setShowTooltip(false);

    trackChatCtaClicked();

    if (isPinningMode && currentPinningSession) {
      const lastPin = pins.length > 0 ? pins[pins.length - 1] : null;
      trackPinningSessionChatOpened({
        sessionId: currentPinningSession.sessionId,
        artworkId: currentPinningSession.artworkId,
        pinsCount: pins.length,
        lastPinState: lastPin?.state ?? null
      });
    }

    const chatWidget = document.getElementById("tidio-chat");
    if (chatWidget) {
      isChatVisible.current = true;

      setTimeout(() => {
        window.tidioChatApi?.show?.();
        window.tidioChatApi?.open?.();
        window.tidioChatApi?.messageFromVisitor?.("I need help!");
      }, 100);
    }
  };

  const handleSaveClick = async () => {
    trackDesignLabCtaClicked("save_and_start_new_design");
    setIsSaveProjectVisible(true);
  };

  return (
    <>
      {!isDesktop && !isEditMode && !isSample && (
        <Save
          className={cn(
            "size-6",
            userType === "guest_user" && "js-sign-in-modal-toggle",
            isSaveDesignDisabled ? "cursor-not-allowed opacity-50" : ""
          )}
          onClick={userType === "guest_user" ? () => {} : handleSaveClick}
        />
      )}
      <TooltipTrigger isOpen={showTooltip}>
        <Button
          data-testid="chat-now-link"
          variant="icon"
          size="icon"
          className="group flex size-auto flex-row items-center gap-2 bg-transparent p-0 shadow-none hover:bg-transparent"
          onPress={onChatClickHandler}
          aria-label="Chat with an expert"
        >
          <MessageCircleMore className="size-6 lg:size-8" />
        </Button>
        <Tooltip
          placement="bottom"
          onClose={() => setShowTooltip(false)}
          onClick={() => {
            setShowTooltip(true);
            onChatClickHandler();
          }}
          className="cursor-default bg-blue-700"
          arrowClassName="fill-blue-700"
        >
          Need help?
        </Tooltip>
      </TooltipTrigger>
    </>
  );
}
